import { createInstance } from '@optimizely/react-sdk'
import { get as getConfig } from 'config'
import { getDeviceType } from 'environment'
import { Map, List } from 'immutable'
import _get from 'lodash/get'
import { getAuthIsLoggedIn } from 'services/auth'
import { EN } from 'services/languages/constants'
import { getLocalStorage, setLocalStorage } from 'services/local-preferences'

const DEFAULT_BATCH_SIZE = 10
const DEFAULT_EVENT_FLUSH_INTERVAL = 1000
const LOCAL_STORAGE_USER_KEY = 'optimizely_feature_user_id'

const config = getConfig()
const optimizelySdkKey = _get(config, 'optimizelyFeatureSdkKey')

let optimizely = null

/**
 * Initializes and returns the Optimizely feature client.
 * This function checks if the client is already initialized to avoid multiple instances.
 * @returns {Object} - The Optimizely feature client instance.
 */
export function getOptimizelyFeatureClient () {
  const shouldInitialize = !optimizely

  if (shouldInitialize) {
    optimizely = createInstance({
      sdkKey: optimizelySdkKey,
      eventBatchSize: DEFAULT_BATCH_SIZE,
      eventFlushInterval: DEFAULT_EVENT_FLUSH_INTERVAL,
      // logLevel: 'debug',
    })
  }

  return optimizely
}

/**
 * Retrieves the Optimizely feature user object.
 * This function constructs a user object with various attributes based on the current state.
 * @param {Object} state - The current application state.
 * @returns {Object} - The Optimizely feature user object.
 */
export function getOptimizelyFeatureUser (state) {
  const {
    auth,
    user,
    resolver,
    optimizelyFeature,
  } = state

  const id = getUserId(user, auth, optimizelyFeature)
  const path = resolver.get('path', null)
  const device = getDeviceType()
  const isLoggedIn = getAuthIsLoggedIn(auth)
  const subscriptions = auth.get('subscriptions') || List()

  const res = {
    id: id.toString(),
    attributes: {
      user_id: user.get('uid') || auth.get('uid') || -1,
      user_country: user.getIn(['data', 'requestCountry']) || auth.get('country') || '',
      user_device: device,
      user_language: user.getIn(['data', 'language', 0], EN),
      user_is_logged_in: isLoggedIn,
      user_active_flags: '',
      user_entitlements: subscriptions.join(', '),
      user_new_signup: false,
      user_time_hour: new Date().getHours(),
      user_time_minute: new Date().getMinutes(),
      page_url: (global && global.location) ? global.location.href : path,
      page_path: path,
      page_referrer: (global && global.document) ? global.document.referrer : '',
    },
  }
  return res
}

/**
 * Creates an Optimizely Feature event.
 * This function tracks an event with the Optimizely feature client.
 * @param {string} eventName - The name of the event to track.
 * @param {string} userId - The ID of the user to associate with the event.
 * @param {Object} tags - Additional tags to include with the event.
 */
export function setOptimizelyFeatureEvent (eventName, userId, tags) {
  const optimizelyClient = getOptimizelyFeatureClient()

  optimizelyClient.onReady().then(() => {
    optimizelyClient.track(eventName, userId, {}, { $opt_event_properties: tags })
  })
}

/**
 * Retrieves the user ID for Optimizely feature tracking.
 * This function checks local storage for a user ID, and if not found,
 * it generates a new anonymous UUID based on the user or auth state.
 * @param {Object} user - The user object from the application state.
 * @param {Object} auth - The auth object from the application state.
 * @returns {string} - The user ID to be used for Optimizely feature tracking.
 */
function getUserId (user = Map(), auth = Map(), optimizelyFeature = Map()) {
  const useAnonUid = optimizelyFeature.get('useAnonUid') || false
  const isLoggedIn = getAuthIsLoggedIn(auth) || false
  const uid = user.get('uid') || auth.get('uid') || null

  if (isLoggedIn && uid && !useAnonUid) {
    return uid
  }

  const localStorageUser = getLocalStorage(LOCAL_STORAGE_USER_KEY)

  if (localStorageUser && localStorageUser.id) {
    return localStorageUser.id
  }

  const anonymousUuid = user.getIn(['data', 'anonymousUuid']) || auth.get('uuid') || ''
  setLocalStorage(LOCAL_STORAGE_USER_KEY, { id: anonymousUuid }, uid, auth)

  return anonymousUuid
}
